import type { ReactNode } from "react";
import { Box } from "../../components/ui/box";
import { Icon } from "../../components/ui/icon";
import { Text } from "../../components/ui/text";
import type { Omittable } from "../../lib/props";
import { WidgetInteractive } from "./widget-interactive";
import { LIST_ROW_PX } from "./widget-list-row";

/**
 * A band's title line inside a widget list: the band's name (`sm`, semibold)
 * on the list row's own rung — the {@link LIST_ROW_PX} line every
 * `WidgetListRow` under it stands on, so a heading and its rows run at one
 * pitch and the column reads as one list with folds in it, not as two
 * heights interleaved — then its exact count as a muted adornment beside it. One geometry for a live band and for a
 * zero band (`WidgetEmptyBands`), so a card that fills keeps the exact rows
 * a reader saw while it was empty.
 *
 * `label` is a string for a plain word band, or a node when the host names
 * the band with its own chip. `muted` is the zero band's weight: the name
 * steps back to the muted ink and the count to the faint ink, so an empty
 * card reads as structure at rest, not as bands shouting nothing.
 *
 * With `onToggle` the line is a disclosure shaped like a collapsible stack
 * section's title (`WidgetCard` in a stack): a CAPSULE around the caret and
 * the name — `caret-down` open, `caret-right` folded — whose wash hugs the
 * words rather than the row, bleeding its `sm` inset into the gutter so the
 * caret itself sits on the content edge above the rows it folds. The count
 * stays OUTSIDE the capsule to its right, the way a stack section's count
 * adornment does, so the wash names exactly what a press acts on. `name` is
 * the band's spoken name for the button ("Collapse Overdue") where `label`
 * is a node; a string label names itself.
 */
export type WidgetBandHeaderProps = Omittable<{
  label: ReactNode;
  count: number;
  muted?: boolean;
  /** Whether the band's rows are showing. Read only with `onToggle`. */
  expanded?: boolean;
  /** Fold or unfold the band. Absent, the line is a plain heading. */
  onToggle?: () => void;
  /** The band's name for the toggle's accessible label when `label` is not a
   *  string. */
  name?: string;
}>;

/** The list row's rung: a band header is the same line as the rows it
 *  folds, so a caret line and a row line never differ by a few pixels. */
const LINE_HEIGHT = LIST_ROW_PX;
const INSET = "sm" as const;

export function WidgetBandHeader({
  label,
  count,
  muted = false,
  expanded = true,
  onToggle,
  name,
}: WidgetBandHeaderProps) {
  const title =
    typeof label === "string" ? (
      <Text size="sm" variant={muted ? "muted" : "default"} weight="semibold">
        {label}
      </Text>
    ) : (
      label
    );
  const tally = (
    <Text size="xs" variant={muted ? "faint" : "muted"} weight="medium" tabular>
      {count.toLocaleString()}
    </Text>
  );
  if (!onToggle) {
    return (
      <Box align="center" direction="row" gap="xs" height={LINE_HEIGHT}>
        {title}
        {tally}
      </Box>
    );
  }
  const spoken = name ?? (typeof label === "string" ? label : undefined);
  return (
    <Box align="center" direction="row" gap="xs">
      <WidgetInteractive
        as="button"
        type="button"
        align="center"
        direction="row"
        gap="xs"
        height={LINE_HEIGHT}
        minWidth={0}
        shrink
        paddingX={INSET}
        marginLeftNegative={INSET}
        rounded="md"
        cursor="pointer"
        hoverBackground
        aria-expanded={expanded}
        aria-label={spoken ? `${expanded ? "Collapse" : "Expand"} ${spoken}` : undefined}
        onClick={onToggle}
      >
        <Icon color="muted" name={expanded ? "caret-down" : "caret-right"} size="sm" />
        {title}
      </WidgetInteractive>
      {tally}
    </Box>
  );
}
